import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";
import SystemNotes from "./SystemNotes";

/* ── Collapsible Section ── */
const Section = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3 border">
      <div className="fw-bold p-2 border-bottom d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer", backgroundColor: "#DFE4EB" }}
        onClick={() => setOpen(!open)}>
        <span>{title}</span><span>{open ? "▾" : "▸"}</span>
      </div>
      {open && <div className="row g-3 p-3">{children}</div>}
    </div>
  );
};

/* ── Read-only info field ── */
const InfoField = ({ label, value, mono = false }) => (
  <div>
    <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 600, textTransform: "uppercase", marginBottom: 3 }}>
      {label}
    </div>
    <div className={`form-control bg-light${mono ? " font-monospace" : ""}`}
      style={{ fontSize: 13, color: "#374151", minHeight: 38 }}>
      {value || "—"}
    </div>
  </div>
);

/* ── Status config ── */
const STATUS_OPTIONS = ["PENDING", "ONGOING", "COMPLETED", "CANCELLED"];
const STATUS_STYLE = {
  PENDING:   { bg: "#fff3cd", color: "#856404" },
  ONGOING:   { bg: "#cfe2ff", color: "#084298" },
  COMPLETED: { bg: "#d1e7dd", color: "#0a3622" },
  CANCELLED: { bg: "#f8d7da", color: "#842029" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLE[status] || { bg: "#e9ecef", color: "#495057" };
  return (
    <span style={{
      display: "inline-block", padding: "3px 12px", borderRadius: 20,
      fontSize: 12, fontWeight: 600, background: s.bg, color: s.color,
    }}>{status || "—"}</span>
  );
};

/* ── Progress bar ── */
const ProgressBar = ({ verified, missing, discrepancy, pending, total }) => {
  if (!total) return null;
  const pct = v => Math.round((v / total) * 100);
  return (
    <div>
      <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", height: 10 }}>
        <div title={`Verified: ${verified}`}    style={{ width: `${pct(verified)}%`,    background: "#198754" }} />
        <div title={`Missing: ${missing}`}      style={{ width: `${pct(missing)}%`,      background: "#dc3545" }} />
        <div title={`Discrepancy: ${discrepancy}`} style={{ width: `${pct(discrepancy)}%`, background: "#fd7e14" }} />
        <div title={`Pending: ${pending}`}      style={{ width: `${pct(pending)}%`,      background: "#dee2e6" }} />
      </div>
      <div className="d-flex gap-3 mt-1" style={{ fontSize: 11, color: "#6b7280" }}>
        <span><span style={{ color: "#198754", fontWeight: 600 }}>■</span> Verified {verified}</span>
        <span><span style={{ color: "#dc3545", fontWeight: 600 }}>■</span> Missing {missing}</span>
        <span><span style={{ color: "#fd7e14", fontWeight: 600 }}>■</span> Discrepancy {discrepancy}</span>
        <span><span style={{ color: "#adb5bd", fontWeight: 600 }}>■</span> Pending {pending}</span>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════ */
export default function EditAuditRecord() {
  const navigate = useNavigate();
  const { id }   = useParams();
  const token    = localStorage.getItem("token");

  /* ── Loaded record ── */
  const [audit,   setAudit]   = useState(null);
  const [loading, setLoading] = useState(true);

  /* ── Editable fields (only what the backend PUT allows) ── */
  const [auditName, setAuditName] = useState("");
  const [status,    setStatus]    = useState("PENDING");

  /* ── UI ── */
  const [submitting, setSubmitting] = useState(false);

  /* ── Tabs ── */
  const [activeTab, setActiveTab] = useState("systemNotes");
  const [visitedTabs, setVisitedTabs] = useState(new Set(["systemNotes"]));
  const [navbarColor, setNavbarColor] = useState("#080235");

  /* ── Bootstrap ── */
  useEffect(() => {
    const bl = document.createElement("link"); bl.rel = "stylesheet"; bl.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css"; document.head.appendChild(bl);
    const ic = document.createElement("link"); ic.rel = "stylesheet"; ic.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css"; document.head.appendChild(ic);
    return () => { bl.remove(); ic.remove(); };
  }, []);

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token)                        { toast.error("You must be logged in."); navigate("/"); return; }
    // if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized.");          navigate("/"); }
  }, [navigate, token]);

  /* ── Navbar Color ── */
  useEffect(() => {
      const userId = localStorage.getItem("userId");
      if (!token || !userId) return;
      fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  /* ── Load audit by ID ── */
  useEffect(() => {
    if (!token || !id) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/asset-audits/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (r.status === 404) throw new Error("Audit record not found.");
        if (!r.ok)            throw new Error("Failed to load audit record.");
        return r.json();
      })
      .then(data => {
        setAudit(data);
        setAuditName(data.auditName || "");
        setStatus(data.status      || "PENDING");
      })
      .catch(err => {
        toast.error(err.message || "Failed to load audit record.");
        navigate("/allauditrecords");
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  /* ── Submit PUT ── */
  const handleSubmit = async e => {
    e.preventDefault();
    if (!auditName.trim()) { toast.error("Audit name is required."); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asset-audits/${id}`, {
        method:  "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body:    JSON.stringify({ auditName: auditName.trim(), status }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to update audit record.");
      }
      toast.success("Audit record updated successfully.");
      setTimeout(() => navigate("/allauditrecords"), 1200);
    } catch (err) {
      toast.error(err.message || "Update failed.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading state ── */
  if (loading) {
    return (
      <>
        <div className="fixed-top"><AssetModuleNavbar /></div>
        <div className="container-fluid" style={{ paddingTop: 120 }}>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" />
            <div className="text-muted mt-3" style={{ fontSize: 14 }}>Loading audit record…</div>
          </div>
        </div>
      </>
    );
  }

  const total       = audit?.totalItems       ?? 0;
  const verified    = audit?.verifiedItems    ?? 0;
  const missing     = audit?.missingItems     ?? 0;
  const discrepancy = audit?.discrepancyItems ?? 0;
  const pending     = audit?.pendingItems     ?? 0;

  /* ═══════════════════════════ RENDER ═══════════════════════════ */
  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top"><AssetModuleNavbar /></div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>

        {/* ── Page Header ── */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h4 className="fw-bold mb-0">
              <i className="bi bi-pencil-square me-2 "></i>
              Edit Audit Record
            </h4>
            <div className="d-flex gap-2 mt-2  align-items-center">
            <button className="btn btn-primary" type="submit" form="edit-audit-form" disabled={submitting} style={{ minWidth: 50 }}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : <> Save</>}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
          </div>
        </div>

        {/* ── Info banner ── */}
        <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-3" style={{ fontSize: 13 }}>
          <i className="bi bi-info-circle-fill"></i>
          <span>
            Audit period, department, and location are <strong>locked</strong> after creation — they determine which assets were added.
            You can update the <strong>audit name</strong> and <strong>status</strong> below.
          </span>
        </div>

        <form id="edit-audit-form" onSubmit={handleSubmit}>

          {/* ── SECTION 1: Progress snapshot (read-only) ── */}
          <Section title="Audit Progress (Read-only)">
            <div className="col-md-2">
              <InfoField label="Audit Period"  value={audit?.auditPeriod} />
            </div>
            <div className="col-md-3">
              <InfoField label="Department"    value={audit?.department} />
            </div>
            <div className="col-md-3">
              <InfoField label="Location"      value={audit?.location} />
            </div>
            <div className="col-md-2">
              <InfoField label="Created By"    value={audit?.createdBy} />
            </div>
            <div className="col-md-2">
              <InfoField label="Created At"    value={audit?.createdAt ? new Date(audit.createdAt).toLocaleDateString() : "—"} />
            </div>

            {/* Item count cards */}
            <div className="col-12">
              <div className="row g-2">
                {[
                  { label: "Total Assets",  value: total,       color: "#1e3a5f", icon: "bi-clipboard2-check" },
                  { label: "Verified",      value: verified,    color: "#198754", icon: "bi-check-circle-fill" },
                  { label: "Missing",       value: missing,     color: "#dc3545", icon: "bi-x-circle-fill" },
                  { label: "Discrepancy",   value: discrepancy, color: "#fd7e14", icon: "bi-exclamation-circle-fill" },
                  { label: "Pending",       value: pending,     color: "#6c757d", icon: "bi-hourglass-split" },
                ].map((s, i) => (
                  <div className="col-md-2 col-4" key={i}>
                    <div className="bg-white border rounded p-2 d-flex align-items-center gap-2">
                      <i className={`bi ${s.icon}`} style={{ color: s.color, fontSize: 18 }}></i>
                      <div>
                        <div style={{ fontSize: 10, color: "#6b7280" }}>{s.label}</div>
                        <div className="fw-bold" style={{ fontSize: 18, color: s.color }}>{s.value}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            {total > 0 && (
              <div className="col-12">
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
                  Completion: {total > 0 ? Math.round(((verified + missing + discrepancy) / total) * 100) : 0}%
                  &nbsp;({verified + missing + discrepancy} of {total} items reviewed)
                </div>
                <ProgressBar
                  verified={verified} missing={missing}
                  discrepancy={discrepancy} pending={pending} total={total}
                />
              </div>
            )}

            {/* Completed-audit warning */}
            {audit?.status === "COMPLETED" && (
              <div className="col-12">
                <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-0" style={{ fontSize: 13 }}>
                  <i className="bi bi-exclamation-triangle-fill text-warning"></i>
                  This audit is already <strong>Completed</strong>. Any changes will be saved.
                </div>
              </div>
            )}
          </Section>

          {/* ── SECTION 2: Editable fields ── */}
          <Section title="Editable Fields">

            {/* Audit Name */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Audit Name <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                value={auditName}
                onChange={e => setAuditName(e.target.value)}
                placeholder="e.g. Q2 IT Asset Verification"
              />
            </div>

            {/* Status */}
            <div className="col-md-3">
              <label className="form-label fw-semibold">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                {STATUS_OPTIONS.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <div className="mt-2">
                <StatusBadge status={status} />
              </div>
            </div>
          </Section>

          {/* ── System Information Tabs ── */}
          <ul className="nav nav-tabs mb-0 fw-semibold mt-4" style={{ backgroundColor: navbarColor }}>
            {[["systemNotes", "System Notes"]].map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(key);
                    setVisitedTabs(prev => new Set(prev).add(key));
                  }}
                  style={{
                    color: activeTab === key ? navbarColor : "#fff",
                    backgroundColor: activeTab === key ? "#fff" : "transparent",
                    fontWeight: activeTab === key ? "600" : "normal",
                  }}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>
          <div className="border p-3 bg-white mb-4">
            <div className="text-muted small mb-2">
              <strong>Date Created:</strong>{" "}
              {audit?.createdAt ? new Date(audit.createdAt).toLocaleString() : "—"}
            </div>
            {visitedTabs.has("systemNotes") && (
              <div style={{ display: activeTab === "systemNotes" ? "block" : "none" }}>
                <SystemNotes entityType="AUDIT" entityId={id} token={token} />
              </div>
            )}
          </div>

          {/* ── Action Buttons ── */}
          <div className="d-flex gap-2 mt-3 mb-5 align-items-center">
            <button className="btn btn-primary" type="submit" disabled={submitting} style={{ minWidth: 50 }}>
              {submitting
                ? <><span className="spinner-border spinner-border-sm me-1" /> Saving…</>
                : <> Save</>}
            </button>
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(-1)}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
